import { ArrowBackRounded, MiscellaneousServicesRounded } from "@mui/icons-material";
import { Box, Card, IconButton, Typography } from "@mui/material";
import { PropsWithChildren } from "react";
import XLSIcon from "../../assets/xls_icon.svg";
import FilterIcon from "../../assets/filter_icon.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import useUser from "../../providers/user/user_provider";
import { useActiveFilterCount } from "../../store/use-services-filter-store";
import { ToolbarButton } from "../../components/toolbar_button/toolbar_button";
import { black, greyEnabled, purple, purpleLight } from "../../theme/colors";
import OrgSelector from "../../components/org_selector/org_selector";
import SortIcon from "../../assets/sort.svg";
import {
  ServicesSortOption,
  servicesSortOptionLabel,
} from "../../views/services/components/sort_services_popup/sort_services_popup";

interface ServiceLayoutProps {
  downloadExcel: () => void;
  setOpenModal: (val: boolean) => void;
  sortOption: ServicesSortOption;
  page: number;
  onOpenSort: () => void;
  setPage: (val: number) => void;
}

export default function ServiceLayout({
  children,
  page,
  setPage,
  downloadExcel,
  setOpenModal,
  sortOption,
  onOpenSort,
}: PropsWithChildren<ServiceLayoutProps>) {
  const activeFilterCount = useActiveFilterCount();

  const [searchParams, setSearchParams] = useSearchParams();
  const { userPermissions } = useUser();
  const handleBackClick = () => {
    const currPage = Number(searchParams.get("page")) || 1;

    if (currPage > 1) {
      const newPage = currPage - 1;
      setPage?.(newPage);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("page", newPage.toString());
          return params;
        },
        { replace: true }
      );
    }
  };

  return (
    <Box display="flex" mt={4}>
      <Card
        elevation={0}
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 700,
          borderRadius: 3,
          border: "1px solid #ECECEC",
        }}
      >
        <Box flex={1} display="flex" flexDirection="column">
          <Box sx={{ p: { xs: 2.5, sm: 3 }, pb: 2, borderBottom: "1px solid #ECECEC" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {userPermissions?.superAdmin && page > 1 && (
                  <IconButton onClick={handleBackClick}>
                    <ArrowBackRounded />
                  </IconButton>
                )}
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: purpleLight,
                      flexShrink: 0,
                    }}
                  >
                    <MiscellaneousServicesRounded sx={{ color: purple, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color={black}>
                      Services
                    </Typography>
                    <Typography variant="body2" color={greyEnabled}>
                      Review and manage submitted service listings.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <OrgSelector lockToOwnOrg />
                <ToolbarButton icon={XLSIcon} label="Export" onClick={downloadExcel} />
                <ToolbarButton
                  icon={SortIcon}
                  label={servicesSortOptionLabel(sortOption)}
                  onClick={onOpenSort}
                />
                <ToolbarButton
                  icon={FilterIcon}
                  label="Filters"
                  badgeCount={activeFilterCount}
                  onClick={() => setOpenModal(true)}
                />
              </Box>
            </Box>
          </Box>

          {children}
        </Box>
      </Card>
    </Box>
  );
}
